import React from "react";
import { makeStyles } from "@material-ui/core/styles";

const CDN = "https://cdn.publer.com/brand/banners/";

interface IBrand {
    name: string;
    logo: string;
    hoverLogo: string;
    href: string;
    target?: string;
    width: number;
    height: number;
}

const brands: IBrand[] = [
    {
        name: "Kalemi",
        logo: `${CDN}kalemi-gray.svg`,
        hoverLogo: `${CDN}kalemi.svg`,
        href: "https://kalemi.co",
        target: "_blank",
        width: 71,
        height: 16,
    },
    {
        name: "Publer",
        logo: `${CDN}publer-gray.svg`,
        hoverLogo: `${CDN}publer.svg`,
        href: "https://publer.com",
        target: "_blank",
        width: 48,
        height: 16,
    },
    {
        name: "Linkie",
        logo: `${CDN}linkie-gray.svg`,
        hoverLogo: `${CDN}linkie.svg`,
        href: "https://linkie.bio",
        target: "_blank",
        width: 52,
        height: 16,
    },
    {
        name: "Kibo",
        logo: `${CDN}kibo-gray.svg`,
        hoverLogo: `${CDN}kibo.svg`,
        href: "https://kibo.ai",
        width: 42,
        height: 10,
    },
];

const useStyles = makeStyles(() => ({
    aside: {
        zIndex: 50,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "40px",
    },
    inner: {
        margin: "0 auto",
        display: "flex",
        height: "100%",
        width: "100%",
        maxWidth: "1304px",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 12px",
    },
    nav: {
        display: "flex",
        height: "32px",
        alignItems: "center",
        gap: "25px",
        borderRadius: "20px",
        backgroundColor: "#fff",
        padding: "0 10px",
    },
    link: {
        display: "flex",
        alignItems: "center",
        position: "relative",
        outline: "none",
        "&:hover $baseImg": {
            opacity: 0,
        },
        "&:hover $hoverImg": {
            opacity: 1,
        },
    },
    logoWrap: {
        position: "relative",
        display: "block",
    },
    img: {
        position: "absolute",
        inset: 0,
        height: "100%",
        width: "100%",
        objectFit: "contain",
        transition: "opacity 300ms ease-out",
    },
    baseImg: {
        opacity: 1,
    },
    hoverImg: {
        opacity: 0,
    },
    divider: {
        height: "16px",
        width: "1px",
        flexShrink: 0,
        backgroundColor: "rgba(87,87,87,0.28)",
    },
}));

interface IKalemiBannerProps {
    items?: IBrand[];
    className?: string;
}

export default function KalemiBanner({ items = brands, className = "" }: IKalemiBannerProps): JSX.Element {
    const classes = useStyles();

    return (
        <aside aria-label="Kalemi products" className={`${classes.aside} ${className}`}>
            <div className={classes.inner}>
                <nav aria-label="Product navigation" className={classes.nav}>
                    {items.map((brand, index) => (
                        <React.Fragment key={brand.name}>
                            <a
                                href={brand.href}
                                target={brand.target}
                                rel={brand.target === "_blank" ? "noreferrer" : undefined}
                                aria-label={`Visit ${brand.name}`}
                                className={classes.link}
                            >
                                <span className={classes.logoWrap} style={{ width: brand.width, height: brand.height }}>
                                    <img
                                        src={brand.logo}
                                        alt={brand.name}
                                        className={`${classes.img} ${classes.baseImg}`}
                                    />
                                    <img
                                        src={brand.hoverLogo}
                                        alt=""
                                        aria-hidden="true"
                                        className={`${classes.img} ${classes.hoverImg}`}
                                    />
                                </span>
                            </a>
                            {index === 0 && <span aria-hidden="true" className={classes.divider} />}
                        </React.Fragment>
                    ))}
                </nav>
            </div>
        </aside>
    );
}
